/**
 * sl-mkx.4.2 + sl-k94y.7.2: SlotDescriptionEditor
 *
 * Shared editor for per-slot, per-locale descriptions of a step template's
 * input/config/output slots. Each slot delegates the locale-keyed text
 * editing to `I18nFieldEditor` so the publish wizard speaks one tab pattern
 * across title, description, and slot fields. AI-Generate stays slot-bound
 * (the prompt carries slot-context — different concern from translate).
 *
 * SOLID:
 * - S: Slot description editing only — no publish submission, no state
 *   outside slotDescriptions
 * - D: Depends on a minimal {id, name, type, required} slot shape and an
 *   explicit availableLocales/defaultLocale/enforcedLocale tuple, not on
 *   the deployment LocaleSettings hook — non-wizard callers can pass the
 *   policy verbatim.
 */
import { notify } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { useState } from 'react';

import { I18nFieldEditor } from './i18n-field-editor';
import { getPrimaryPublishDescription } from './publish-shared';
import * as styles from './slot-description-editor.css';

export interface SlotRef {
  id: string;
  name: string;
  type: string;
  /**
   * sl-k94y.7.2: mirrors the backend `iterateSlots` predicate — only
   * required slots gate the cross-deployment locale baseline. Frontend
   * applies the same predicate for the required-marker so the UX matches
   * what the publish endpoint will reject. Defaults to `false` for
   * legacy callers that haven't been updated yet.
   */
  required?: boolean;
}

export interface SlotDescriptionsState {
  /** slotId → { locale → description } */
  [slotId: string]: Record<string, string>;
}

export interface SlotDescriptionEditorProps {
  workspaceId: string | undefined;
  inputSlots: readonly SlotRef[];
  configSlots: readonly SlotRef[];
  outputSlots: readonly SlotRef[];
  /**
   * sl-k94y.7.2: deployment-resolved locale policy. Wizard threads this
   * through from `LocaleSettingsService` so the slot-editor doesn't need
   * its own picker hook.
   */
  availableLocales: ReadonlyArray<string>;
  defaultLocale: string;
  enforcedLocale: string;
  /** Top-level descriptions used to feed the AI suggest prompt. */
  descriptionsI18n: Record<string, string>;
  title: string;
  category: string;
  value: SlotDescriptionsState;
  onChange: (next: SlotDescriptionsState) => void;
  disabled?: boolean;
}

export function SlotDescriptionEditor({
  workspaceId,
  inputSlots,
  configSlots,
  outputSlots,
  availableLocales,
  defaultLocale,
  enforcedLocale,
  descriptionsI18n,
  title,
  category,
  value,
  onChange,
  disabled,
}: SlotDescriptionEditorProps) {
  const t = useI18n();
  const [busySlotId, setBusySlotId] = useState<string | null>(null);

  const groups = [
    {
      kind: 'input' as const,
      label: t['com.streamlain.marketplace.node-publish.slots.inputs'](),
      slots: inputSlots,
    },
    {
      kind: 'config' as const,
      label: t['com.streamlain.marketplace.node-publish.slots.config'](),
      slots: configSlots,
    },
    {
      kind: 'output' as const,
      label: t['com.streamlain.marketplace.node-publish.slots.outputs'](),
      slots: outputSlots,
    },
  ].filter(group => group.slots.length > 0);

  async function generateSlotDescription(
    slot: SlotRef,
    groupKind: 'input' | 'config' | 'output',
    otherSlots: readonly SlotRef[]
  ) {
    if (!workspaceId) return;
    try {
      setBusySlotId(slot.id);
      const response = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/marketplace/suggest-descriptions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entityType: 'step',
            title,
            category,
            summary: getPrimaryPublishDescription(
              descriptionsI18n,
              enforcedLocale
            ),
            // sl-k94y.7.2: ask for every deployment locale so the slot
            // descriptions stay aligned with the deployment policy
            // (same shape the I18nFieldEditor renders).
            languages: availableLocales,
            slotContext: {
              slotName: slot.name,
              slotType: slot.type,
              slotKind: groupKind,
              otherSlotHints: otherSlots
                .filter(candidate => candidate.id !== slot.id)
                .map(candidate => `${candidate.name} (${candidate.type})`),
            },
          }),
        }
      );
      if (!response.ok) {
        throw new Error(
          t['com.streamlain.marketplace.node-publish.slot.error']()
        );
      }
      const data = (await response.json()) as {
        descriptionsI18n?: Record<string, string>;
      };
      if (!data.descriptionsI18n) {
        throw new Error(
          t['com.streamlain.marketplace.node-publish.slot.no-payload']()
        );
      }
      onChange({
        ...value,
        [slot.id]: {
          ...(value[slot.id] ?? {}),
          ...data.descriptionsI18n,
        },
      });
    } catch (error) {
      notify.error({
        title: t['com.streamlain.marketplace.node-publish.slot.error'](),
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setBusySlotId(null);
    }
  }

  if (groups.length === 0) return null;

  return (
    <div className={styles.editorRoot}>
      {groups.map(group => (
        <div key={group.kind} className={styles.slotGroup}>
          <span className={styles.groupLabel}>{group.label}</span>
          {group.slots.map(slot => {
            // sl-k94y Phase 4b R3 (Cluster B): the slot baseline applies
            // for every visibility — backend's `validatePublishedSlots`
            // no longer early-returns for deployment-bound publishes, so
            // the wizard's required-marker is always live for required
            // slots.
            const slotRequiredLocales: ReadonlyArray<string> = slot.required
              ? [enforcedLocale]
              : [];
            return (
              <div
                key={slot.id}
                className={styles.slotCard}
                id={`publish-slot-${slot.id}`}
              >
                <div className={styles.slotHeader}>
                  <div className={styles.slotTitle}>
                    {slot.name} ({slot.type})
                  </div>
                  <Button
                    size="default"
                    disabled={
                      disabled || busySlotId === slot.id || !workspaceId
                    }
                    onClick={() =>
                      void generateSlotDescription(
                        slot,
                        group.kind,
                        group.slots
                      )
                    }
                  >
                    {busySlotId === slot.id
                      ? t[
                          'com.streamlain.marketplace.node-publish.slot.generating'
                        ]()
                      : t[
                          'com.streamlain.marketplace.node-publish.slot.generate'
                        ]()}
                  </Button>
                </div>
                <I18nFieldEditor
                  value={value[slot.id] ?? {}}
                  onChange={next =>
                    onChange({
                      ...value,
                      [slot.id]: next,
                    })
                  }
                  locales={availableLocales}
                  defaultLocale={defaultLocale}
                  label={t[
                    'com.streamlain.marketplace.node-publish.slot.placeholder'
                  ]({
                    slot: slot.name,
                    language: enforcedLocale.toUpperCase(),
                  })}
                  placeholder={t[
                    'com.streamlain.marketplace.node-publish.slot.placeholder'
                  ]({
                    slot: slot.name,
                    language: enforcedLocale.toUpperCase(),
                  })}
                  multiline
                  requiredLocales={slotRequiredLocales}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
